import classNames from 'classnames';
import styles from './SessionDefinition.module.css';
import {
  Form,
  Input,
  DatePicker,
  DatePickerProps,
  Button,
  Row,
  Col,
  MenuProps,
  Space,
  Dropdown,
  List,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import type { FormInstance } from 'antd/es/form';
import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import Modal from '../../Modal';
import Icon from 'src/components/Icon/Icon';
import { getSearchResultFromQueryParameter } from 'src/app/query.service';
import PageLoader from 'src/components/PageLoader';

type PostData =
  | {
      dateFrom: string | undefined;
      dateTo: string | undefined;
      sourceSoftwareVersion: string | null;
      sourceType: Array<string> | null;
    }
  | [];
interface Source {
  sourceType: string;
  id: string;
}

interface SourceTypeVersion {
  sourceSoftwareVersion: string;
  sourceType: string;
  id: string;
}

interface SessionDefinitionProps {
  isActive: boolean;
  onNext: (newData: { [key: string]: unknown }) => void;
  form: FormInstance; // Pass the form instance from the parent
  queryType: string;
}

// const onChange: DatePickerProps['onChange'] = (date, dateString) => { };
const SessionDefinition = forwardRef<any, SessionDefinitionProps>(
  ({ isActive, onNext, form, queryType }, ref) => {
    const [openModal, setOpenModal] = useState(false);
    const [inputPlaceholder, setInputPlaceholder] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [isSmallText, setIsSmallText] = useState(false);
    const [sources, setSources] = useState<Source[]>([]);
    const [sourceTypeVersion, setSourceTypeVersion] = useState<SourceTypeVersion[]>([]);
    const [sourceCheck, setSourceCheck] = useState<string>('');
    const [searchValue, setSearchValue] = useState<string>('');
    const inputRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isNextActive, setIsNextActive] = useState(false);
    const [selectedValues, setSelectedValues] = useState<{ [key: string]: string[] }>({
      sourceType: [],
      sourceSwVersion: [],
    });
    const today = dayjs();
    const oneMonthAgo = dayjs().subtract(1, 'month').add(1, 'day');
    useImperativeHandle(ref, () => ({
      reset() {
        form.setFieldValue('dateFrom', oneMonthAgo);
        form.setFieldValue('dateTo', today);
        setSelectedValues({
          sourceType: [],
          sourceSwVersion: [],
        });
      },
    }));
    const [isLoading, setIsLoading] = useState(false);
    const formatDateToDDMMYYYY = (dateString: string): string => {
      const dateObject = new Date(dateString);
      const year = dateObject.getFullYear();
      const month = String(dateObject.getMonth() + 1).padStart(2, '0'); // January is 0!
      const day = String(dateObject.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const handleRemove = (type: string, itemRemove: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type].filter((item) => item !== itemRemove),
      }));
    };

    const applyMethod = (type: string) => {
      if (type === 'sourceType') {
        form.setFieldValue('sourceType', selectedValues.sourceType.join(', '));
      }
      if (type === 'sourceSwVersion') {
        form.setFieldValue('sourceSWVersion', selectedValues.sourceSwVersion.join(', '));
      }
      setOpenModal(false);
    };

    const handleDateChange = () => {
      const dateFrom = form.getFieldValue('dateFrom');
      const dateTo = form.getFieldValue('dateTo');

      if (dateFrom && dateTo) {
        const diff = dayjs(dateTo).diff(dayjs(dateFrom), 'days');

        if (diff > 365) {
          setErrorMessage('Invalid date range! (Max 365 days)');
          setIsNextActive(true);
        } else {
          setErrorMessage('');
          setIsNextActive(false);
        }
      }
    };

    const handleInputClick = async (placeholder: string) => {
      setInputPlaceholder(placeholder);
      const dateFromValue: string = form.getFieldValue('dateFrom') as string;
      const dateToValue: string = form.getFieldValue('dateTo') as string;
      const formattedDateFrom: string = formatDateToDDMMYYYY(dateFromValue);
      const formattedDateTo: string = formatDateToDDMMYYYY(dateToValue);
      const postData: PostData = {
        dateFrom: formattedDateFrom,
        dateTo: formattedDateTo,
        sourceSoftwareVersion: (form.getFieldValue('sourceSoftwareVersion') as string) || null,
        sourceType: selectedValues.sourceType,
      };
      form.setFieldValue('filterValue', '');

      if (placeholder === 'Source Type') {
        setSourceCheck('sourceType');
        setOpenModal(true);
        setSearchValue('');
        try {
          const searchParameter = 'sourceType';
          setIsLoading(true);
          const result: Source[] = await getSearchResultFromQueryParameter<Source>(
            queryType,
            searchParameter,
            postData
          );
          setSources(result);
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          console.error('Error fetching data:', error);
        }
      } else if (placeholder === 'Source Sw Version') {
        setSourceCheck('sourceSwVersion');
        setSearchValue('');
        setOpenModal(true);
        try {
          const searchParameter = 'sourceSoftwareVersion';
          setIsLoading(true);

          const result: SourceTypeVersion[] =
            await getSearchResultFromQueryParameter<SourceTypeVersion>(
              queryType,
              searchParameter,
              postData
            );
          setSourceTypeVersion(result);
          setIsLoading(false);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };

    const onLinkClick = (type: string, value: string) => {
      setSelectedValues((prevValues) => ({
        ...prevValues,
        [type]: prevValues[type].includes(value) ? prevValues[type] : [...prevValues[type], value],
      }));
    };

    const handleModalInputFocus = () => {
      setIsInputFocused(true);
      setIsSmallText(true);
    };

    const handleModalInputBlur = () => {
      if (searchValue.length <= 0) {
        setIsInputFocused(false);
        setIsSmallText(false);
      }
    };

    const handleInputClear = (input: string) => {
      if (input === `Source Type`) {
        form.setFieldValue('sourceType', '');
        setSelectedValues((prevValues) => ({
          ...prevValues,
          sourceType: [],
        }));
      } else if (input === `Source Sw Version`) {
        form.setFieldValue('sourceSWVersion', '');
        setSelectedValues((prevValues) => ({
          ...prevValues,
          sourceSwVersion: [],
        }));
      }
    };

    const handleNext = () => {
      const dateFromValue: string = form.getFieldValue('dateFrom') as string;
      const dateToValue: string = form.getFieldValue('dateTo') as string;
      const formattedDateFrom: string = formatDateToDDMMYYYY(dateFromValue);
      const formattedDateTo: string = formatDateToDDMMYYYY(dateToValue);

      const normalize = (value: string[] | undefined): string[] | null => {
        return !value || value.length === 0 ? null : value;
      };
      const data = {
        dateFrom: formattedDateFrom,
        dateTo: formattedDateTo,
        sourceSoftwareVersion: normalize(selectedValues.sourceSwVersion),
        sourceType: normalize(selectedValues.sourceType),
      };
      onNext(data);
    };

    const handlePlaceholderClick = () => {
      // Focus the input when the placeholder is clicked
      inputRef.current.focus();
    };

    const handleReset = () => {
      form.setFieldValue('dateFrom', oneMonthAgo);
      form.setFieldValue('dateTo', today);
      form.setFieldValue('sourceType', '');
      form.setFieldValue('sourceSWVersion', '');
      setSelectedValues({
        sourceType: [],
        sourceSwVersion: [],
      });
    };

    const shouldShowResetButton = Object.values(selectedValues).some((value) => {
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });
    const moreItems: MenuProps['items'] = [
      {
        key: '1',
        label: '5',
      },
      {
        key: '2',
        label: '10',
      },
      {
        key: '3',
        label: '15',
      },
      {
        key: '4',
        label: '25',
      },
      {
        key: '4',
        label: '50',
      },
      {
        key: '4',
        label: '70',
      },
    ];

    const filteredSources = sources.filter((source) =>
      source.sourceType.toLowerCase().includes(searchValue.toLowerCase())
    );
    const filteredSourcesTypeVersion = sourceTypeVersion.filter((source) =>
      source.sourceSoftwareVersion?.toLowerCase().includes(searchValue.toLowerCase())
    );

    return (
      <div className={classNames(styles.SessionDefinition)}>
        <div>
          <div
            className={`
              ${styles['SessionDefinition--QueryTitle']} 
              ${
                isActive
                  ? styles['SessionDefinition--active']
                  : styles['SessionDefinition--InActive']
              }
              `}
            // style={{ backgroundColor: isActive ? '#000000' : '#dcdcdc' }}
          >
            Session Definition
            {shouldShowResetButton && (
              <div style={{ display: 'inline', float: 'right', marginRight: '30px' }}>
                <Button
                  className={`${
                    isActive
                      ? styles['SessionDefinition--Reset']
                      : styles['SessionDefinition--InActive']
                  }`}
                  type="primary"
                  htmlType="submit"
                  onClick={handleReset}
                  disabled={!isActive}
                >
                  Reset
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className={classNames(styles['SessionDefinition--FormContainer'])}>
          <Form
            form={form}
            name="session_definition_data_form"
            layout="horizontal"
            disabled={!isActive}
            initialValues={{
              dateFrom: oneMonthAgo,
              dateTo: today,
            }}
          >
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="dateFrom"
                  rules={[{ required: true, message: 'Please select the start date!' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Date From"
                    format="DD/MM/YYYY"
                    onChange={handleDateChange}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name="dateTo"
                  rules={[{ required: true, message: 'Please select the end date!' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Date To"
                    format="DD/MM/YYYY"
                    onChange={handleDateChange}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  name="sourceType"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Source Type')}
                    placeholder="Source Type"
                    suffix={
                      selectedValues.sourceType?.length > 0 && (
                        <span onClick={() => handleInputClear('Source Type')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.sourceType?.length > 0 && (
                  <span className={`${styles['SessionDefinition--Placeholder']} ${styles.small}`}>
                    Source Type ({selectedValues.sourceType?.length})
                  </span>
                )}
              </Col>
              <Col span={6}>
                <Form.Item
                  name="sourceSWVersion"
                  rules={[{ required: true, message: 'Please enter a value!' }]}
                >
                  <Input
                    readOnly
                    onClick={() => handleInputClick('Source Sw Version')}
                    placeholder="Source SW Version"
                    suffix={
                      selectedValues.sourceSwVersion?.length > 0 && (
                        <span onClick={() => handleInputClear('Source Sw Version')}>
                          <Icon name="close" className={`${styles.clearIcon}`} />
                        </span>
                      )
                    }
                  />
                </Form.Item>
                {selectedValues.sourceSwVersion?.length > 0 && (
                  <span className={`${styles['SessionDefinition--Placeholder']} ${styles.small}`}>
                    Source Type ({selectedValues.sourceSwVersion?.length})
                  </span>
                )}
              </Col>
            </Row>
            {errorMessage && (
              <div className={classNames(styles['SessionDefinition--error-message'])}>
                <p>{errorMessage}</p>
              </div>
            )}
            {isActive && (
              <Row justify="end">
                <Col>
                  <Form.Item>
                    <Button
                      className={classNames(styles['SessionDefinition--FormButton'])}
                      type="primary"
                      htmlType="submit"
                      onClick={handleNext}
                      disabled={isNextActive}
                      style={{
                        backgroundColor: !isNextActive ? 'black' : '#000', // Keep it black
                        color: 'white', // Ensure text remains white
                        opacity: !isNextActive ? 1 : 0.5, // Reduce opacity but keep text visible
                      }}
                    >
                      Next
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Modal
              open={openModal}
              onClose={() => {
                setOpenModal(false);
                setSearchValue('');
              }}
              width={900}
              footer={null}
            >
              <div className={classNames(styles['SessionDefinition--ModalHeader'])}>
                Select {inputPlaceholder}
              </div>
              <div className={classNames(styles['SessionDefinition--ModelContent'])}>
                <div>
                  <p className={classNames(styles['SessionDefinition--TagTitle'])}>Selection</p>
                  <div className={classNames(styles['SessionDefinition--TagsContainer'])}>
                    {(selectedValues[sourceCheck] || []).map((item, index) => {
                      return (
                        <Button
                          type="primary"
                          key={`${item}${index}`}
                          onClick={() => handleRemove(sourceCheck, item)}
                          className={styles['selected-button']}
                        >
                          {item}
                          <Icon
                            name="close"
                            className={`${styles['SessionDefinition--ModalDropDownIcon']} ${styles['close-icon']}`}
                          />
                        </Button>
                      );
                    })}
                  </div>
                </div>
                <div className={styles['SessionDefinition--Search']}>
                  <Row gutter={16} justify="space-between">
                    <Col span={8}>
                      <div style={{ position: 'relative' }}>
                        <Input
                          name="filterValue"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          onFocus={handleModalInputFocus}
                          onBlur={handleModalInputBlur}
                          ref={inputRef} // Use ref to access the input
                        />
                        <span
                          className={`${styles['SessionDefinition--ModalInputText']} ${
                            isSmallText ? styles.small : ''
                          }`}
                          onClick={handlePlaceholderClick} // Click handler to focus the input
                        >
                          Look for {inputPlaceholder}
                        </span>
                      </div>
                    </Col>
                    <Col span={4}>
                      <Dropdown menu={{ items: moreItems }} trigger={['click']}>
                        <Button className={styles['SessionDefinition--ModalDropDown']}>
                          Limit
                          <Icon
                            name="arrow_drop_down"
                            className={styles['SessionDefinition--ModalDropDownIcon']}
                          />
                        </Button>
                      </Dropdown>
                    </Col>
                  </Row>
                </div>
                <div className={styles['SessionDefinition--SearchList']}>
                  {sourceCheck === 'sourceType' && (
                    <List
                      itemLayout="horizontal"
                      // loading={isLoading}
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredSources}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.sourceType.includes(item.sourceType)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('sourceType', item.sourceType)}
                        >
                          <span>{item.sourceType}</span>
                        </List.Item>
                      )}
                    />
                  )}
                  {sourceCheck === 'sourceSwVersion' && (
                    <List
                      itemLayout="horizontal"
                      // loading={isLoading}
                      loading={{
                        spinning: isLoading,
                        indicator: <Spin className={styles['loader-color']} />,
                      }}
                      dataSource={filteredSourcesTypeVersion}
                      renderItem={(item) => (
                        <List.Item
                          className={`${
                            selectedValues.sourceSwVersion.includes(item.sourceSoftwareVersion)
                              ? styles['selected-item']
                              : styles['list-item']
                          }`}
                          onClick={() => onLinkClick('sourceSwVersion', item.sourceSoftwareVersion)}
                        >
                          <span>{item.sourceSoftwareVersion}</span>
                        </List.Item>
                      )}
                    />
                  )}
                </div>
              </div>
              <div className={styles['SessionDefinition--ModalFooter']}>
                <Space>
                  <Button
                    onClick={() => setOpenModal(false)}
                    className={styles['SessionDefinition--CancelButton']}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => applyMethod(sourceCheck)}
                    className={styles['SessionDefinition--Buttons']}
                  >
                    Apply
                  </Button>
                </Space>
              </div>
            </Modal>
          </Form>
        </div>
      </div>
    );
  }
);
SessionDefinition.displayName = 'SessionDefinition';
export default SessionDefinition;
